import * as React from 'react';
import { Form } from 'react-bootstrap';
import dateFormat from 'dateformat';
import StyledModal from '../StyledModal';
import LoaderWheel from '../LoaderWheel';
import { UserSendEmailSelection } from '../UserSendEmailSelection';
import { UserSendSmsSelection } from '../UserSendSmsSelection';
import Loader from '../Loader';
import type { UserSoa } from '../../../MedicareProAPI/Entities/User/UserSoa';
import type { User } from '../../../MedicareProAPI/Entities/User/User';
import type { UserSoaStatus } from '../../../MedicareProAPI/Entities/User/UserSoaStatus';
import type { Ind } from '../../../MedicareProAPI/Entities/Ind/Ind';
import { UserSoaRepository } from '../../../MedicareProAPI/Repositories/User/UserSoaRepository';
import { UserSoaStatusRepository } from '../../../MedicareProAPI/Repositories/User/UserSoaStatusRepository';
import { IndRepository } from '../../../MedicareProAPI/Repositories/Ind/IndRepository';
import { WebAPI } from '../../../MedicareProAPI/API/WebAPI';

export interface CmsFormSingleProps {
  props: any;
  formData: UserSoa;
  handleFormDelete: (indId: number, soaId: number) => void;
  onEmailSend: (data: any, userSoa: UserSoa, individual: Ind, agent: User) => Promise<string>;
  onSmsSend: (data: any, soa: UserSoa, agent: User) => Promise<string>;
  onEmailSendCopy: (data: any, userSoa: UserSoa, individual: Ind, agent: User) => Promise<string>;
  onSmsSendCopy: (data: any, soa: UserSoa, agent: User) => Promise<string>;
  onFormPrintF2f: (soa: UserSoa) => Promise<string>;
  onUpdateCode: (indId: number, soaId: number, payload: any) => Promise<UserSoa>;
  onFormUpdate: (indId: number, soaId: number, payload: any) => Promise<UserSoa>;
  onSaveFormStatus: (message: string, soaId: number) => Promise<UserSoaStatus>;
}
/**
 * Interaction with a saved CMS form
 *
 * @author Andrew Brown <abrown@medicareprocrm.com>
 */
export const CmsFormSingle = (props: CmsFormSingleProps) => {
  const [formData, setFormData] = React.useState(props.formData);
  const thisProps = props.props;

  const [editedFormData, setEditedFormData] = React.useState<any>();

  const [formHistoryData, setFormHistoryData] = React.useState<Array<UserSoaStatus>>();

  const [formIsSaving, setFormIsSaving] = React.useState<boolean>(false);
  const [formIsDeleting, setFormIsDeleting] = React.useState<boolean>(false);
  const [isEditingForm, setIsEditingForm] = React.useState<boolean>(false);
  const [isViewingHistory, setIsViewingHistory] = React.useState<boolean>(false);
  const [isFetchingHistory, setIsFetchingHistory] = React.useState<boolean>(false);
  const [readyToResend, setReadyToResend] = React.useState<boolean>(false);
  const [isResending, setIsResending] = React.useState<boolean>(false);
  const [isSending, setIsSending] = React.useState<boolean>(false);
  const [sendingMethodIsValid, setSendingMethodIsValid] = React.useState<boolean>(false);
  const [agentIsSigning, setAgentIsSigning] = React.useState<boolean>(false);

  const [sendingType, setSendingType] = React.useState<string>('resend');

  const [sendingMethod, setSendingMethod] = React.useState<string>();
  const [sendFailureMessage, setSendFailureMessage] = React.useState<string>(null);
  const [sendingData, setSendingData] = React.useState<any>();
  const [accessCode, setAccessCode] = React.useState<string>();

  const [individual, setInd] = React.useState<Ind>();
  const [selectedAgent, setSelectedAgent] = React.useState<User>();
  const [agents, setAgents] = React.useState<Array<User>>();

  const methodOfContact = ['Phone', 'Email', 'Text', 'Mail', 'In-person', 'Walk-in', 'Other'];
  const badgeClass =
    formData.getSoaState() === 'completed'
      ? 'ml-2 badge badge-success'
      : 'ml-2 badge badge-primary';

  const userSoaRepository = new UserSoaRepository();
  userSoaRepository.setAccessToken(props.props.userAccessToken);

  const userSoaStatusRepository = new UserSoaStatusRepository();
  userSoaStatusRepository.setAccessToken(props.props.userAccessToken);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.props.userAccessToken);

  const sendingOptions = [
    { label: 'Email', value: 'email' },
    { label: 'SMS', value: 'sms' },
  ];

  const date = new Date();
  const thisYear = date.getFullYear();
  const nextYear = thisYear + 1;
  const reviewDatesOptions = [thisYear, nextYear];

  const visitortime = new Date();
  const visitortimezone = visitortime.getTimezoneOffset() / 60;

  /**
   * Handles form change of form edit
   */
  const handleFormChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;

    const _editedFormData = { ...editedFormData, [name]: value };
    setEditedFormData(_editedFormData);
  };

  /**
   * Handles clicking on edit of form
   */
  const handleEditFormClick = () => {
    let editFormData;

    if (formData.getSoaType() == 'SOA') {
      console.log('original date', formData.getSoaAppointmentDate());
      const appDate = new Date(formData.getSoaAppointmentDate());
      const month = appDate.getMonth() + 1;
      const appointmentDate =
        appDate.getFullYear() +
        '-' +
        String(month).padStart(2, '0') +
        '-' +
        String(appDate.getDate()).padStart(2, '0');
      console.log('appointment date:', appointmentDate);
      editFormData = {
        soaAppointmentDate: appointmentDate,
        soaInitialContactMethod: formData.getSoaInitialContactMethod() || 'Phone',
        soaDiscussedPlans: formData.getSoaDiscussedPlans(),
      };
      console.log(editFormData);
    }

    if (formData.getSoaType() == 'Consent (B)') {
      editFormData = {
        consentCompany: formData.getConsentCompany(),
        consentEffective: formData.getConsentEffective()
          ? formData.getConsentEffective().split('/')[2] +
            '-' +
            formData.getConsentEffective().split('/')[0] +
            '-' +
            formData.getConsentEffective().split('/')[1]
          : '',
        consentPlan: formData.getConsentPlan(),
        consentSubsidy: formData.getConsentSubsidy(),
        consentPremium: formData.getConsentPremium(),
      };
    }

    setEditedFormData(editFormData);
    setIsEditingForm(true);
  };

  /**
   * Handles updating the form
   */
  const handleFormUpdate = () => {
    setFormIsSaving(true);

    if (editedFormData?.soaAgentName && formData.getSoaAgentSignature()) {
      editedFormData.updateAgent = true;
      editedFormData.soaAgentSignature = editedFormData?.soaAgentName;
    }

    props.onFormUpdate(thisProps.indId, formData.getSoaId(), editedFormData).then((newForm) => {
      setFormData(newForm);
      setFormIsSaving(false);
      setIsEditingForm(false);
    });
  };

  const handleFormSign = () => {
    setAgentIsSigning(true);
    const signedForm = {
      soaAgentSignature: formData.getSoaAgentName(),
      soaAgentTimezoneOffset: visitortimezone,
    };

    props.onFormUpdate(thisProps.indId, formData.getSoaId(), signedForm).then((newForm) => {
      setFormData(newForm);
      setAgentIsSigning(false);
    });
  };

  /**
   * Handles changing form to completed or pending on form dropdown
   */
  const handleMarkForm = (status) => {
    const updatedData = { soaState: status };

    props.onFormUpdate(thisProps.indId, formData.getSoaId(), updatedData).then((newForm) => {
      setFormData(newForm);
    });
  };

  /**
   * Handles triggering sending and choosing mechanism
   */
  const handleSend = (type) => {
    if (type == 'copy') {
      setIsResending(true);
      handleGetInd();
      setSendingType('copy');
    }

    if (type == 'resend') {
      setIsResending(true);
      handleGetInd();
      setSendingType('resend');
    }
  };

  /**
   * Handles resending the form via selected sending method
   */
  const handleFormResend = () => {
    setIsSending(true);

    props
      .onUpdateCode(props.props.indId, formData.getSoaId(), {
        soaTransmissionMethod: sendingData.method,
      })
      .then((newForm) => {
        const statusMessage =
          'Form resent via ' +
          sendingData.method +
          ' by agent' +
          '. Access Code: ' +
          newForm.getCode();
        setFormData(newForm);
        // save the status
        props.onSaveFormStatus(statusMessage, newForm.getSoaId()).then((response) => {
          let sendingResponse;

          if (newForm.getSoaTransmissionMethod() === 'email') {
            sendingResponse = props.onEmailSend(sendingData, newForm, individual, selectedAgent);
          }
          if (newForm.getSoaTransmissionMethod() === 'sms') {
            sendingResponse = props.onSmsSend(sendingData, newForm, selectedAgent);
          }
          if (['print', 'f2f'].includes(newForm.getSoaTransmissionMethod())) {
            sendingResponse = props.onFormPrintF2f(newForm);
          }

          // send/open the form
          sendingResponse.then((response) => {
            setIsSending(false);
            setIsResending(false);
          });
        });
      });
  };

  /**
   * Handles sending a copy of the document to beneficiary
   */
  const handleFormSendCopy = () => {
    setIsSending(true);

    let sendingResponse;

    if (sendingMethod === 'email') {
      sendingResponse = props.onEmailSendCopy(sendingData, formData, individual, selectedAgent);
    }
    if (sendingMethod === 'sms') {
      sendingResponse = props.onSmsSendCopy(sendingData, formData, selectedAgent);
    }

    // send/open the form
    sendingResponse.then((response) => {
      setIsSending(false);
      setIsResending(false);
    });
  };

  /**
   * Handles deleting the form
   */
  const handleFormDelete = () => {
    const r = confirm('Do you really want to delete this?');

    if (r) {
      setFormIsDeleting(true);
      props.handleFormDelete(props.props.indId, formData.getSoaId());
    }
  };

  /**
   * Handles fetching form status
   */
  const handleViewHistory = () => {
    isViewingHistory === true ? setIsViewingHistory(false) : setIsViewingHistory(true);

    userSoaStatusRepository
      .getIndSoaStatus(props.props.indId, { criteria: { soaId: formData.getSoaId() } })
      .then((response) => {
        setFormHistoryData(response.getItems());

        let _accessCode = '';
        if (response.getItems().length > 0) {
          response.getItems().forEach((item) => {
            if (item.getSoaStatus().indexOf('Access Code:') >= 0) {
              const temp = item.getSoaStatus().split('Access Code:');
              _accessCode = temp[1];
            }
          });
        }

        setAccessCode(_accessCode);
      });
  };

  /**
   * Handles is form is valid to be updated
   */
  const formIsValid = () => {
    let isValid = true;
    if (
      editedFormData.soaAppointmentDate == '' ||
      editedFormData.soaInitialContactMethod == '' ||
      editedFormData.soaDiscussedPlans == ''
    ) {
      isValid = false;
    }
    return isValid;
  };

  /**
   * Sets the sending payload data
   * * @param {any} response response of sending method data
   */
  const handleSendingMethodSelections = (response) => {
    setSendingData(response);

    if (['print', 'f2f'].includes(response.method)) {
      setSendingMethodIsValid(true);
    }
  };

  /**
   * Handles validating if the sending data is sendable
   * @param {boolean} isValid
   */
  const handleSendingMethodIsValid = (isValid) => {
    setSendingMethodIsValid(isValid);

    if (isValid) {
      setSendFailureMessage(null);
    }
  };

  const handleExternalView = async (url) => {
    const isFullUrl = (str) => {
      try {
        const parsed = new URL(str);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
      } catch {
        return false;
      }
    };

    if (!isFullUrl(url)) {
      // AWS case: wait for signed URL
      try {
        const response = await webAPI.uploadSignUrl(
          'documents/' + url,
          props.props.userId,
          'medicareprocrm',
          'inline'
        );
        url = response.file.url;
      } catch (error) {
        console.error('Error getting signed URL:', error);
        return;
      }
    }

    console.log('Opening:', url);

    // Use window.open instead — more reliable in this context
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  /**
   * Gets individual that belongs to form
   * @returns {Promise<Ind>} Promise resolving once the event is completed
   */
  const handleGetInd = () => {
    //setIsResending(true);

    if (props.props.indId) {
      const individualRepository = new IndRepository();
      individualRepository.setAccessToken(props.props.userAccessToken);

      individualRepository
        .getInd(props.props.indId, { include: ['phones', 'spouse', 'emails', 'loa', 'owner'] })
        .then((individual) => {
          // Get available agents
          const agents = [];
          agents.push(individual.getOwnerId());
          if (individual.getLoa()) {
            agents.push(individual.getLoa());
          }

          // Preselect an agent
          const selectedAgent = agents.find(
            (agent) => agent.getUserId() == formData.getUser().getUserId()
          );
          console.log('sel agents: ', selectedAgent);
          setAgents(agents);
          setInd(individual);
          setSelectedAgent(selectedAgent);
          setReadyToResend(true);
        });
    }
  };

  const handleAgentChange = (event) => {
    const index = event.target.value;
    console.log(index);
    const agent = agents.find((item) => item.getUserId() == index);
    console.log(agent);

    const _editedFormData = {
      ...editedFormData,
      ['user']: agent.getUserId(),
      ['soaAgentName']: agent.getUserName(),
      ['soaAgentPhone']: agent.getUserPhone(),
    };

    setEditedFormData(_editedFormData);
  };

  return (
    <>
      <StyledModal
        size="lg"
        title={'Resend ' + formData.getSoaType()}
        footerCloseButton={true}
        show={isResending}
        onClose={() => setIsResending(false)}
      >
        <div className="row pl-1 pr-1">
          <div className="col-12 p-2 font-weight-bold mb-2 boldHeaderLabel">
            How do you want to send the {formData.getSoaType()}?
          </div>
          <div className="col-12">
            {sendingOptions.map((sendOption, index) => {
              return (
                <div className="form-check">
                  <input
                    key={index}
                    className="form-check-input"
                    type="radio"
                    data-id="text"
                    value={sendOption.value}
                    name="soaTransmissionMethod"
                    checked={sendingMethod === sendOption.value}
                    onChange={() => setSendingMethod(sendOption.value)}
                  />
                  <label className="form-check-label">{sendOption.label}</label>
                </div>
              );
            })}
          </div>
          <div className="col-12 mt-2">
            {sendingMethod === 'email' && (
              <UserSendEmailSelection
                props={props.props}
                ind={individual}
                user={selectedAgent}
                onChange={handleSendingMethodSelections}
                isValid={handleSendingMethodIsValid}
              />
            )}

            {sendingMethod === 'sms' && (
              <UserSendSmsSelection
                props={props.props}
                ind={individual}
                user={selectedAgent}
                onChange={handleSendingMethodSelections}
                isValid={handleSendingMethodIsValid}
              />
            )}
          </div>
          <div className="col-12 mt-2">
            <button
              onClick={sendingType == 'resend' ? handleFormResend : handleFormSendCopy}
              type="button"
              className="btn btn-sm border border-success btn-success mt-3"
            >
              {isSending && (
                <>
                  <i className="far fa-spinner fa-spin mr-1"></i>Sending...
                </>
              )}
              {!isSending && <>Send</>}
            </button>
          </div>
        </div>
      </StyledModal>

      {editedFormData !== undefined && (
        <StyledModal
          size="lg"
          title={'Edit ' + formData.getSoaType()}
          footerCloseButton={true}
          show={isEditingForm}
          onClose={(e) => setIsEditingForm(false)}
        >
          {agents && agents.length > 1 && (
            <div className="row gutter-2">
              <div className="col-12 col-lg-4 mb-2">
                <label className="label">Agent</label>
                <select
                  className="form-control form-control-sm"
                  value={editedFormData.user || formData.getUser().getUserId()}
                  onChange={handleAgentChange}
                >
                  {agents.map((option, index) => {
                    return <option value={option.getUserId()}>{option.getUserName()}</option>;
                  })}
                </select>
              </div>
            </div>
          )}

          {formData.getSoaType() == 'SOA' && (
            <div className="row gutter-2">
              <div className="col-12">
                <label className="label">Appointment Date</label>
                <input
                  type="date"
                  className="keep form-control form-control-sm needsSlashes"
                  name="soaAppointmentDate"
                  value={editedFormData.soaAppointmentDate}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="col-12 mt-2">
                <label className="label">Method of Contact</label>
                <select
                  className="form-control form-control-sm"
                  name="soaInitialContactMethod"
                  onChange={handleFormChange}
                >
                  {methodOfContact.map((method) => {
                    return (
                      <option selected={method === editedFormData.soaInitialContactMethod}>
                        {method}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="col-12 mt-2 ">
                <label className="label">Plans Discussed</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  name="soaDiscussedPlans"
                  value={editedFormData.soaDiscussedPlans}
                  onChange={handleFormChange}
                />
              </div>
            </div>
          )}

          {formData.getSoaType() == 'Consent (B)' && (
            <>
              <div className="row gutter-2">
                <div className="col-12 p-2 font-weight-bold mt-3 mb-2 boldHeaderLabel">
                  Income Information
                </div>
                <div className="col-12 col-lg-4 mb-2">
                  <label className="label">Household Income</label>
                  <input
                    type="text"
                    className="form-control form-control-sm keep"
                    name="soaIncome"
                    required
                    value={editedFormData.soaIncome}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="col-12 col-lg-4 mb-2">
                  <label className="label">Income Year</label>
                  <select
                    className="form-control form-control-sm"
                    name="soaIncomeYear"
                    required
                    onChange={handleFormChange}
                  >
                    {reviewDatesOptions.map((option) => {
                      return (
                        <option selected={editedFormData.soaIncomeYear == option ? true : false}>
                          {option}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="col-12 col-lg-4 mb-2">
                  <label className="label">Application Review Date</label>
                  <input
                    type="date"
                    className="keep form-control form-control-sm needsSlashes"
                    name="soaReviewDate"
                    value={editedFormData.soaReviewDate}
                    onChange={handleFormChange}
                    required
                  />
                </div>
              </div>
              <div className="row gutter-2">
                <div className="col-12 p-2 font-weight-bold border-top border-info mt-3 mb-2 boldHeaderLabel">
                  Plan Information
                </div>
                <div className="col-12 col-lg-4 mb-2">
                  <label className="label">Insurance Company</label>
                  <input
                    type="text"
                    className="form-control form-control-sm keep"
                    name="consentCompany"
                    required
                    value={editedFormData.consentCompany}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="col-12 col-lg-8 mb-2">
                  <label className="label">Insurance Plan</label>
                  <input
                    type="text"
                    className="form-control form-control-sm keep"
                    name="consentPlan"
                    required
                    value={editedFormData.consentPlan}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="col-12 col-lg-4 mb-2">
                  <label className="label">Policy Effective</label>
                  <input
                    type="date"
                    className="keep form-control form-control-sm needsSlashes"
                    name="consentEffective"
                    value={editedFormData.consentEffective}
                    onChange={handleFormChange}
                    required
                  />
                </div>
                <div className="col-12 col-lg-4 mb-2">
                  <label className="label">Applied Subsidy</label>
                  <input
                    type="text"
                    className="form-control form-control-sm keep"
                    name="consentSubsidy"
                    required
                    value={editedFormData.consentSubsidy}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="col-12 col-lg-4 mb-2">
                  <label className="label">Monthly Premium for Insured</label>
                  <input
                    type="text"
                    className="form-control form-control-sm keep"
                    name="consentPremium"
                    required
                    value={editedFormData.consentPremium}
                    onChange={handleFormChange}
                  />
                </div>
              </div>
            </>
          )}
          <div className="row gutter-2">
            <button
              disabled={formIsValid() === false}
              onClick={handleFormUpdate}
              type="button"
              className="btn btn-sm border border-success btn-success mt-3 ml-3"
            >
              {!formIsSaving && <>Update</>}

              {formIsSaving && (
                <>
                  <i className="far fa-spinner fa-spin mr-1"></i>Updating...
                </>
              )}
            </button>
          </div>
        </StyledModal>
      )}

      <div className="col-12">
        <div
          className="card drugCard mb-3  pt-2 mx-2 pb-0 border border-secondary"
          style={{ height: 'auto' }}
        >
          <div className="card-header phoneHead">
            <span className=" proCrdTitle addressTypesTitle">
              {!formData.isExternal() && (
                <>
                  <span
                    className="float-right dropdown-toggle cp"
                    id="addressDropdown"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    <i className="fas fa-ellipsis-h "></i>
                  </span>
                  <div className="dropdown-menu" aria-labelledby="#addressDropdown">
                    {(formData.getSoaType() === 'SOA' ||
                      formData.getSoaType() == 'Consent (B)') && (
                      <a
                        className="dropdown-item"
                        onClick={() => {
                          handleEditFormClick();
                          handleGetInd();
                        }}
                        style={{ fontSize: '9pt' }}
                      >
                        Edit
                      </a>
                    )}

                    {formData.getSoaTransmissionMethod() === 'print' &&
                      formData.getSoaState() === 'completed' && (
                        <a
                          className="dropdown-item"
                          style={{ fontSize: '9pt' }}
                          onClick={() => handleMarkForm('pending')}
                        >
                          Mark as pending
                        </a>
                      )}

                    {formData.getSoaTransmissionMethod() === 'print' &&
                      formData.getSoaState() !== 'completed' && (
                        <a
                          className="dropdown-item"
                          style={{ fontSize: '9pt' }}
                          onClick={() => handleMarkForm('completed')}
                        >
                          Mark as completed
                        </a>
                      )}

                    {formData.getSoaState() === 'pending' &&
                      !['print', 'f2f', 'Portal'].includes(formData.getSoaTransmissionMethod()) && (
                        <a
                          className="dropdown-item"
                          onClick={() => handleSend('resend')}
                          style={{ fontSize: '9pt' }}
                        >
                          Resend
                        </a>
                      )}

                    {formData.getSoaState() === 'completed' && (
                      <a
                        className="dropdown-item"
                        onClick={() => handleSend('copy')}
                        style={{ fontSize: '9pt' }}
                      >
                        Send Copy
                      </a>
                    )}

                    {(formData.getSoaState() === 'pending' || !formData.getSoaAgentSignature()) && (
                      <a
                        className="dropdown-item text-danger"
                        onClick={handleFormDelete}
                        style={{ fontSize: '9pt' }}
                      >
                        Delete
                      </a>
                    )}
                  </div>
                </>
              )}

              {formData.isExternal() && (
                <>
                  <span
                    className="float-right dropdown-toggle cp"
                    id="addressDropdown"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    <i className="fas fa-ellipsis-h "></i>
                  </span>
                  <div className="dropdown-menu" aria-labelledby="#addressDropdown">
                    {formData.getSoaState() === 'completed' && (
                      <a
                        className="dropdown-item"
                        style={{ fontSize: '9pt' }}
                        onClick={() => handleMarkForm('pending')}
                      >
                        Mark as pending
                      </a>
                    )}

                    {formData.getSoaState() !== 'completed' && (
                      <a
                        className="dropdown-item"
                        style={{ fontSize: '9pt' }}
                        onClick={() => handleMarkForm('completed')}
                      >
                        Mark as completed
                      </a>
                    )}

                    <a
                      className="dropdown-item text-danger"
                      onClick={handleFormDelete}
                      style={{ fontSize: '9pt' }}
                    >
                      Delete
                    </a>
                  </div>
                </>
              )}
              <span>
                {formIsDeleting && <LoaderWheel></LoaderWheel>}
                {formData.getSoaType()} : {formData.getSoaCreatedDate()}{' '}
                <span className="badge data-badge badge-secondary ml-2">
                  {formData.getSoaTransmissionMethod()}
                </span>
              </span>
              {formData.getSoaState() == 'pending' && !formData.isExternal() && (
                <div style={{ fontSize: '8pt' }} className="ml-2 badge badge-primary">
                  Pending Beneficiary Signature
                </div>
              )}
              {formData.getSoaState() == 'completed' &&
                !formData.getSoaAgentSignature() &&
                !formData.getSoaAgentSignaturePath() &&
                !formData.isExternal() && (
                  <div style={{ fontSize: '8pt' }} className="ml-2 badge badge-warning">
                    Pending Agent Signature
                  </div>
                )}
              {formData.getSoaState() == 'completed' &&
                (formData.getSoaAgentSignature() || formData.getSoaAgentSignaturePath()) && (
                  <div style={{ fontSize: '8pt' }} className="ml-2 badge badge-success">
                    Completed
                  </div>
                )}
              {formData.getSoaState() == 'completed' && formData.isExternal() && (
                <div style={{ fontSize: '8pt' }} className="ml-2 badge badge-success">
                  Completed
                </div>
              )}

              <br />
              {}
              {!formData.isExternal() && (
                <div
                  className="badge border border-info text-info cp mt-2"
                  onClick={handleViewHistory}
                  style={{ fontSize: '8pt' }}
                >
                  History
                </div>
              )}
              {formData.isExternal && formData.getSoaExternalSrc() && (
                <div
                  className="badge mt-2 ml-2 border border-primary cp "
                  style={{ fontSize: '8pt' }}
                >
                  <span onClick={() => handleExternalView(formData.getSoaExternalSrc())}>View</span>
                </div>
              )}

              {!formData.isExternal() && (
                <div
                  className="badge mt-2 ml-2 border border-primary cp "
                  style={{ fontSize: '8pt' }}
                >
                  <a
                    target="_blank"
                    href={
                      formData.isExternal() ? formData.getSoaExternalSrc() : formData.getUrlAgent()
                    }
                    rel="noreferrer"
                  >
                    View
                  </a>
                </div>
              )}

              {formData.getSoaState() == 'completed' &&
                !formData.getSoaAgentSignature() &&
                !formData.getSoaAgentSignaturePath() &&
                !agentIsSigning &&
                !formData.isExternal() && (
                  <div
                    style={{ fontSize: '8pt' }}
                    className="ml-2 badge badge-warning"
                    onClick={() => handleFormSign()}
                  >
                    SIGN
                  </div>
                )}
              {agentIsSigning && (
                <span className="ml-2">
                  <i className="far fa-spinner fa-spin"></i> Signing...
                </span>
              )}
            </span>
          </div>
          {isViewingHistory && (
            <div className="card-body pt-2 pb-2 mb-0 soaHistoryDiv" style={{ fontSize: '10pt' }}>
              <ul className="font-weight-normal">
                {((formData.getSoaState() === 'completed' && formData.getSoaAgentSignaturePath()) ||
                  formData.getSoaAgentSignature()) && (
                  <li>{formData.getSoaBeneficiarySignDate()} - Agent signed document.</li>
                )}
                {formData.getSoaState() === 'completed' &&
                  !['print', 'f2f', 'Portal'].includes(formData.getSoaTransmissionMethod()) && (
                    <li>
                      {formData.getSoaBeneficiarySignDate()} - Beneficiary logged in with access
                      code and signed document.
                    </li>
                  )}
                {formData.getSoaState() === 'completed' &&
                  ['f2f'].includes(formData.getSoaTransmissionMethod()) && (
                    <li>
                      {formData.getSoaBeneficiarySignDate()} - Beneficiary signed document
                      face-to-face.
                    </li>
                  )}
                {formData.getSoaState() === 'completed' &&
                  ['Portal'].includes(formData.getSoaTransmissionMethod()) && (
                    <li>
                      {formData.getSoaBeneficiarySignDate()} - Beneficiary signed document from
                      client portal.
                    </li>
                  )}
                {['email', 'sms'].includes(formData.getSoaTransmissionMethod()) && (
                  <li>
                    {formData.getSoaCreatedDate()} - Document sent to beneficiary{' '}
                    {formData.getSoaTransmissionMethod()}: {formData.getSoaTransmissionAddress()}
                  </li>
                )}
                <li>
                  {formData.getSoaCreatedDate()} - Document created.
                  {accessCode && formData.getSoaState() == 'pending' && (
                    <span> Access Code:{accessCode}</span>
                  )}
                </li>
                {/*
                                    {formData.getSoaBeneficiaryIp() ? 'Beneficiary IP:'+formData.getSoaBeneficiaryIp() : ''}
                                     {formData.getSoaBeneficiaryIp() ? 'Beneficiary IP:'+formData.getSoaBeneficiaryIp() : ''}
                                      {formData.getSoaBeneficiaryIp() ? 'Agent IP:'+formData.getSoaAgentIp() : ''}
                                    formHistoryData !== undefined && 
                                    formHistoryData.map(item => {
                                        return <li>{item.getSoaStatusDate()} - {item.getSoaStatus()}</li>
                                    })
                                    */}

                {isFetchingHistory && <LoaderWheel></LoaderWheel>}
              </ul>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
